/**
 * Landing v3 — the phone layout's geometry, as plain functions: where the
 * hero's frame sits on a screen and how its chips drift (landing-phone-hero),
 * how much of the top bar a width holds (landing-phone-bar), and how deep each
 * feature card sits in the pile at a given scroll (landing-phone).
 */

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

// ── The hero ─────────────────────────────────────────────────────────────────

/** The frames' screen (Figma 116:885): the iPhone 13 (390×844) under Safari's
 *  two bars. */
export const HERO_FRAME = { w: 390, h: 723 } as const;

/** Where the frame sits on this screen: scale `k`, offset `ox`/`oy`, and the
 *  screen's size. The frame is fitted whole, between 75% and 115%. */
export type HeroBox = { k: number; ox: number; oy: number; w: number; h: number };

export function fitBox(w: number, h: number): HeroBox {
  const k = Math.min(1.15, Math.max(0.75, Math.min(w / HERO_FRAME.w, h / HERO_FRAME.h)));
  return { k, ox: (w - HERO_FRAME.w * k) / 2, oy: (h - HERO_FRAME.h * k) / 2, w, h };
}

/** A chip's ambient drift: the loop's half-widths (px), its tilt swing (deg),
 *  one loop (s), and where in it the chip first starts (0–1). */
export type Drift = { ax: number; ay: number; r: number; s: number; phase: number };

/** Sixteen points round the drift's ellipse, from rest back to rest — so a
 *  loop restarted at its beginning takes up from where the chip rests. */
export function driftKeyframes(d: Drift): { transform: string }[] {
  return Array.from({ length: 17 }, (_, i) => {
    const a = (i / 16) * Math.PI * 2;
    const x = Math.abs(d.ax * Math.sin(a)) < 0.005 ? 0 : d.ax * Math.sin(a);
    const y =
      Math.abs((d.ay / 2) * (Math.cos(a) - 1)) < 0.005
        ? 0
        : (d.ay / 2) * (Math.cos(a) - 1);
    const r = Math.abs(d.r * Math.sin(a)) < 0.005 ? 0 : d.r * Math.sin(a);
    return {
      transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${r.toFixed(2)}deg)`,
    };
  });
}

// ── The top bar ──────────────────────────────────────────────────────────────

/** How much of the bar a width holds (Figma 116:972: the mark · Study Tools ⌄
 *  · About Us · the language flag): 0 everything, 1 with the menu under its
 *  shorter name ("Features"), 2 with About Us folded into the menu, 3 with
 *  the menu down to its icon. The flag always stays. */
export type BarFit = 0 | 1 | 2 | 3;

/** Between the bar's groups; the ghost buttons' own padding does the rest. */
export const BAR_GAP = 0;
/** After the mark, the frame's first group. */
export const BAR_MARK_GAP = 13;
/** The mark's width (`h-[22px] w-[20.3px]`). */
export const BAR_MARK_WIDTH = 20.3;

/** The controls' natural widths, px. `cta` is 0 where the bar has none. */
export type BarWidths = {
  studyTools: number;
  features: number;
  icon: number;
  about: number;
  flag: number;
  cta: number;
};

/** The fullest bar that fits `room` px (the bar's widest inner width). */
export function pickBarFit(w: BarWidths, room: number): BarFit {
  const start = BAR_MARK_WIDTH + BAR_MARK_GAP;
  const end = w.flag + w.cta;
  const needs = [
    start + w.studyTools + BAR_GAP + w.about + BAR_GAP + end,
    start + w.features + BAR_GAP + w.about + BAR_GAP + end,
    start + w.features + BAR_GAP + end,
  ];
  // Half a pixel of grace: widths are fractional, the room is whole.
  const fit = needs.findIndex((need) => need <= room + 0.5);
  return (fit === -1 ? 3 : fit) as BarFit;
}

// ── The pile ─────────────────────────────────────────────────────────────────

export type PileGeometry = {
  /** Where each card starts in the page's flow, document px. */
  tops: number[];
  /** A pinned card's top on screen, and every card's size. */
  rest: number;
  height: number;
  width: number;
};

/** How far card `k` has slid over the card before it at scroll `y`: 0 as its
 *  top meets that card's bottom, 1 as it pins. */
export function pileCover(g: PileGeometry, k: number, y: number): number {
  if (k <= 0 || k >= g.tops.length || g.height <= 0) return 0;
  return clamp01((y - (g.tops[k] - g.rest - g.height)) / g.height);
}

/** How deep card `i` sits in the pile at scroll `y`: a level per card over
 *  it, counted four deep (deeper, a card is long gone). */
export function pileDepth(g: PileGeometry, i: number, y: number): number {
  let depth = 0;
  for (let k = i + 1; k < Math.min(g.tops.length, i + 5); k += 1) {
    const cover = pileCover(g, k, y);
    if (cover <= 0) break;
    depth += cover;
  }
  return depth;
}

/** The scroll at which card `i` lies on top of the pile, at rest. */
export const pileRestAt = (g: PileGeometry, i: number) => (g.tops[i] ?? 0) - g.rest;

// ── A demo on a phone card ───────────────────────────────────────────────────

/** Air around a staged demo, inside the card's room for it. */
const STAGE_MARGIN = 12;
const STAGE_MIN_SCALE = 0.5;

/** Where a stage sits in its room, and at what scale (and height: a short
 *  room shortens a stage that fills its box). */
export function stageFit(
  stage: { w: number; h: number; minH?: number },
  room: { w: number; h: number },
  margin = STAGE_MARGIN,
): { x: number; y: number; h: number; scale: number } {
  const aw = room.w - 2 * margin;
  const ah = room.h - 2 * margin;
  let h = stage.h;
  let scale = Math.min(1, aw / stage.w, ah / h);
  if (scale < STAGE_MIN_SCALE && stage.minH) {
    h = Math.min(stage.h, Math.max(stage.minH, ah / STAGE_MIN_SCALE));
    scale = Math.min(1, aw / stage.w, ah / h);
  }
  return { x: (room.w - stage.w * scale) / 2, y: (room.h - h * scale) / 2, h, scale };
}
